import React, { useEffect, useRef, useState } from "react";
import { fetchChat, sendChat } from "../lib/api";
import { Send, Users } from "lucide-react";
import { toast } from "sonner";

const NAME_KEY = "gsb_chat_name";

export const ChatSection = ({ streamInfo }) => {
    const [messages, setMessages] = useState([]);
    const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const kickChatUrl =
        streamInfo?.chat_url || "https://kick.com/popout/leonardthepreacher/chat";

    const load = async () => {
        try {
            const data = await fetchChat();
            setMessages(data);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => {
        load();
        // Polling fallback (in case WS is unavailable / dropped)
        const id = setInterval(load, 8000);

        // Real-time WebSocket push
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backend.replace(/^http/, "ws") + "/api/ws/chat";
        let ws = null;
        let reconnectTimer = null;
        let heartbeat = null;
        let closed = false;

        const connect = () => {
            try {
                ws = new WebSocket(wsUrl);
            } catch {
                reconnectTimer = setTimeout(connect, 4000);
                return;
            }
            ws.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    if (!msg || !msg.id) return;
                    setMessages((prev) => {
                        if (prev.some((m) => m.id === msg.id)) return prev;
                        return [...prev, msg].slice(-200);
                    });
                } catch {
                    /* ignore */
                }
            };
            ws.onopen = () => {
                heartbeat = setInterval(() => {
                    if (ws && ws.readyState === 1) {
                        try {
                            ws.send("ping");
                        } catch {
                            /* ignore */
                        }
                    }
                }, 25000);
            };
            ws.onclose = () => {
                if (heartbeat) clearInterval(heartbeat);
                if (!closed) reconnectTimer = setTimeout(connect, 4000);
            };
            ws.onerror = () => {
                try {
                    ws && ws.close();
                } catch {
                    /* ignore */
                }
            };
        };
        connect();

        return () => {
            closed = true;
            clearInterval(id);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (heartbeat) clearInterval(heartbeat);
            if (ws) {
                try {
                    ws.close();
                } catch {
                    /* ignore */
                }
            }
        };
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const submit = async (e) => {
        e.preventDefault();
        const trimmedName = name.trim();
        const trimmedText = text.trim();
        if (!trimmedName) {
            toast.error("Drop a name first 🙏");
            return;
        }
        if (!trimmedText) return;
        setSending(true);
        try {
            localStorage.setItem(NAME_KEY, trimmedName);
            const msg = await sendChat({ username: trimmedName, message: trimmedText });
            setMessages((m) => [...m, msg]);
            setText("");
        } catch (err) {
            toast.error("Couldn't send. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="chat"
            data-testid="chat-section"
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        >
            <div className="mb-8">
                <p className="font-graffiti text-skyblue text-2xl -rotate-2 mb-1">
                    chop it up ↓
                </p>
                <h2 className="font-anton uppercase text-4xl md:text-6xl text-white leading-none">
                    The <span className="text-hotpink">Chat Wall</span>
                </h2>
                <p className="font-chivo text-white/60 text-sm mt-2 max-w-2xl">
                    Two ways to talk: the official Kick chat on the left, or the open
                    Site Wall on the right. Be respectful. Be loud.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Kick official chat */}
                <div className="relative">
                    <Badge color="kickgreen" text="Kick · Official" />
                    <div className="border-4 border-black brutal-shadow-blue bg-black h-[560px] overflow-hidden">
                        <iframe
                            data-testid="kick-chat-iframe"
                            title="Kick Chat"
                            src={kickChatUrl}
                            className="w-full h-full"
                            style={{ border: 0 }}
                        />
                    </div>
                </div>

                {/* Site chat */}
                <div className="relative">
                    <Badge color="hotpink" text="Site · Wall" />
                    <div
                        data-testid="site-chat"
                        className="border-4 border-black brutal-shadow-pink bg-surface h-[560px] flex flex-col"
                    >
                        <div className="px-4 py-3 border-b-2 border-white/10 flex items-center gap-2">
                            <Users className="w-4 h-4 text-kickgreen" />
                            <span className="font-anton uppercase tracking-wider text-sm">
                                Live Site Chat
                            </span>
                            <span className="ml-auto text-xs font-chivo text-white/40">
                                {messages.length} msg
                            </span>
                        </div>

                        <div
                            ref={scrollRef}
                            data-testid="site-chat-messages"
                            className="flex-1 overflow-y-auto p-4 space-y-3"
                        >
                            {messages.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center">
                                    <span className="font-graffiti text-skyblue text-3xl -rotate-2 mb-2">
                                        be first ✦
                                    </span>
                                    <p className="font-chivo text-white/40 text-sm">
                                        No messages yet. Start the wave.
                                    </p>
                                </div>
                            ) : (
                                messages.map((m) => (
                                    <ChatBubble key={m.id} msg={m} self={m.username === name} />
                                ))
                            )}
                        </div>

                        <form
                            onSubmit={submit}
                            data-testid="site-chat-form"
                            className="border-t-2 border-white/10 p-3 grid grid-cols-[120px_1fr_auto] gap-2"
                        >
                            <input
                                data-testid="site-chat-name"
                                aria-label="Your name"
                                placeholder="Name"
                                value={name}
                                maxLength={40}
                                onChange={(e) => setName(e.target.value)}
                                className="bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white placeholder:text-white/30 focus:border-kickgreen focus:outline-none"
                            />
                            <input
                                data-testid="site-chat-input"
                                aria-label="Message"
                                placeholder="Say something to the congregation…"
                                value={text}
                                maxLength={500}
                                onChange={(e) => setText(e.target.value)}
                                className="bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white placeholder:text-white/30 focus:border-skyblue focus:outline-none"
                            />
                            <button
                                data-testid="site-chat-send"
                                disabled={sending}
                                className="bg-kickgreen text-black font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#FF2E93] transition-all disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" strokeWidth={3} />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Badge = ({ color, text }) => {
    const map = {
        kickgreen: "bg-kickgreen text-black",
        hotpink: "bg-hotpink text-white",
    };
    return (
        <div
            className={`absolute -top-3 left-4 z-10 ${map[color]} font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black`}
        >
            {text}
        </div>
    );
};

const colorForName = (s) => {
    const colors = ["#53FC18", "#FF2E93", "#0AC3FF", "#FACC15", "#A78BFA"];
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return colors[h % colors.length];
};

const ChatBubble = ({ msg, self }) => {
    if (msg.is_admin) {
        return (
            <div
                data-testid="site-chat-bubble"
                className="font-chivo text-sm leading-relaxed bg-kickgreen/10 border-l-2 border-kickgreen pl-3 py-1.5 -mx-1"
            >
                <span className="inline-flex items-center gap-1 font-anton uppercase tracking-wider text-xs text-kickgreen mr-2">
                    <span className="text-hotpink">👑</span> {msg.username}
                </span>
                <span className="text-white break-words">{msg.message}</span>
            </div>
        );
    }
    const color = colorForName(msg.username || "anon");
    return (
        <div data-testid="site-chat-bubble" className="font-chivo text-sm leading-relaxed">
            <span
                className="font-anton uppercase tracking-wider text-xs mr-2"
                style={{ color }}
            >
                {msg.username}
            </span>
            <span className="text-white/90 break-words">{msg.message}</span>
        </div>
    );
};
